// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { Typography, CircularProgress } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { getAllFoldersThunk, moveProjectToFolderThunk } from 'Features/folderList/folderListThunk';
import { PROJECTS_SECTION_TITLE, PROJECTS_NOT_FOUND_LABEL } from 'Utils/constants';
import { ClearSelectedFolderAction } from 'Features/folderList/folderListActions';
import PageToolbar from './PageToolbar';
import FolderListItem from '../folderList/FolderListItem';
import stylesDef from './styles';
import { SetPageAction, SetProjectsAction, SetShowMapviewAction } from './projectListActions';
import { GetProjectsThunk } from './projectListThunk';
import useMapCoordinates from './useMapCoordinates';
import MapRail from './MapRail';
import ProjectListItem from './ProjectListItem';
import TermsAndConditionsDialog from './Terms&ConditionsDialog';
import { selectFolderList, selectSelectedFolder } from '../folderList/folderListSelector';
import { selectEffectiveOrderSortFilter } from './projectListSelector';

// Lazy: the ArcGIS SDK (and its theme CSS) is one of the heaviest imports in the app, and
// most dashboard visits never open map view - it loads on the first map-view open instead.
const EsriDashboardMap = lazy(() => import('Features/map/EsriDashboardMap'));

type EmptyState = 'no-projects' | 'not-found' | null;

const ProjectList = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {
    projectsLoading, projectsCount, loadAll, page, list, keywordFilter,
    showMapview, mapCoordinates, mapCoordinatesLoading, mapMissingLocationCount,
  } = useAppSelector(
    (state) => state.projectList,
  );
  const effectiveOrderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);
  const styles = stylesDef(theme, showMapview);
  const folderList = useAppSelector(selectFolderList);
  const { html } = useAppSelector(
    (state) => state.auth,
  );
  const selectedFolder = useAppSelector(selectSelectedFolder);

  useMapCoordinates(showMapview);

  useEffect(() => {
    if (selectedFolder?._id) {
      dispatch(ClearSelectedFolderAction());
    }
  }, [selectedFolder]);

  // The map rail resets the grid's data on search/filter changes but defers the fetch (its
  // responses would render nothing while the map is up). Returning to grid view with an
  // emptied list fetches page 0 here; the infinite-scroll sentinel only loads later pages.
  // State is read at run time through a thunk: the rail search field's unmount flush can
  // empty the list in this same commit, after this closure captured the old values.
  useEffect(() => {
    if (showMapview) return;
    dispatch((innerDispatch: typeof dispatch, getState: () => { projectList: IProjectListState }) => {
      const { list: listNow, projectsLoading: loadingNow, loadAll: loadAllNow } = getState().projectList;
      if (listNow.length === 0 && !loadingNow && !loadAllNow) {
        innerDispatch(GetProjectsThunk() as never);
      }
    });
  }, [showMapview]);

  // In the store rather than component state, so the chosen view survives navigating within the
  // tab during the session.
  const showMapviewClick = () => {
    dispatch(SetShowMapviewAction(true));
  };

  const showGridviewClick = () => {
    dispatch(SetShowMapviewAction(false));
  };

  const handleMoveProjectToFolderSuccess = async (project?: IProject, folderId?: string) => {
    // Update the project's folderId with the new folderId
    let updatedProjects = list.map((p: IProject) => (p._id === project?._id ? { ...p, folderId } : p));
    if (effectiveOrderSortFilter === 'unassigned') {
      updatedProjects = updatedProjects.filter((p: IProject) => !p?.folderId);
    } else if (effectiveOrderSortFilter === 'assigned') {
      // Symmetric case: unassigning a project (e.g. via the new "Unassign" option) while viewing
      // Assigned Projects should drop it from view too, same as assigning one drops it from
      // Unassigned Projects above.
      updatedProjects = updatedProjects.filter((p: IProject) => Boolean(p?.folderId));
    }
    await dispatch(SetProjectsAction(updatedProjects));
  };

  const isSearching = keywordFilter.length > 0;
  const resultsCaption = `${projectsCount} ${projectsCount === 1 ? 'result' : 'results'} for “${keywordFilter}”`;
  const notFoundMessage = `No ${PROJECTS_NOT_FOUND_LABEL[effectiveOrderSortFilter] || 'projects'} found for “${keywordFilter}”`;

  // While searching, folderList mixes two kinds of folders in one array: folders whose name
  // matched the keyword (genuine search results) and folders that merely contain a matched
  // project (present only so the project rows' folder badge can resolve them). Only the former
  // render as folder results; the filter mirrors the server's case-insensitive substring match
  // on folder name (folder_search.js), applied client-side because the server response does not
  // distinguish the two kinds.
  const visibleFolders = isSearching
    ? folderList.filter((folder: IFolder) => (folder.name || '').toLowerCase().includes(keywordFilter.toLowerCase()))
    : folderList;

  // Zero search matches must still say "not found", not the no-projects-in-account message -
  // projectsCount reflects the search-match count while searching, so it can legitimately be 0
  // for an account that has other projects. A single derivation (rather than two independently
  // computed, only-by-convention-exclusive booleans) guarantees exactly one empty state renders,
  // and both share the same in-flight-refetch guard. A folder-name match is a search result,
  // so it suppresses the not-found message even when no project matched.
  const emptyState: EmptyState = (() => {
    if (projectsLoading || list.length > 0) return null;
    if (!isSearching && projectsCount === 0) return 'no-projects';
    if (isSearching && visibleFolders.length > 0) return null;
    return 'not-found';
  })();
  const wrapperStyle : {} = showMapview ? styles.contentWrapperMap : styles.contentWrapperGrid;
  const listStyle : {} = showMapview ? styles.mapList : styles.gridList;
  const listWrapperStyle : {} = showMapview ? styles.mapListWrapper : styles.gridListWrapper;
  const showMapEmptyState = showMapview && !mapCoordinatesLoading && mapCoordinates.length === 0;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id && over?.id) {
      const response = await dispatch(moveProjectToFolderThunk(String(active.id), String(over.id)));
      if (response.success) {
        handleMoveProjectToFolderSuccess(response.data as IProject, String(over.id));
      }
    }
  };

  // Map view swaps the folder/list content area for the map, keeping the top chrome. Every
  // project matching the current search/filter that has a location is plotted; the ones that
  // don't are counted rather than silently dropped.
  if (showMapview) {
    return (
      <Box sx={wrapperStyle}>
        <Box sx={styles.mapviewContent}>
          {html !== undefined && html !== 'undefined' && html !== 'null' && html !== null && (<TermsAndConditionsDialog />)}
          <Box sx={styles.mapviewBody}>
            <MapRail />
            <Box sx={styles.mapArea}>
              <Box component="button" type="button" sx={styles.mapCloseButton} onClick={showGridviewClick}>
                Close Map View
              </Box>
              <Suspense fallback={<Box sx={styles.mapArea} />}>
                <EsriDashboardMap />
              </Suspense>
              {showMapEmptyState && (
                <Box sx={styles.mapEmptyState}>
                  <Typography variant="h3">No projects with a location to show</Typography>
                  <Typography variant="body1">Projects appear here once they have a mapped site.</Typography>
                </Box>
              )}
              {mapMissingLocationCount > 0 && (
                <Box sx={styles.mapMissingLocation}>
                  {`${mapMissingLocationCount} ${mapMissingLocationCount === 1 ? 'project' : 'projects'} without location`}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={wrapperStyle}>
      <Box sx={styles.dashboardContent}>
        {html !== undefined && html !== 'undefined' && html !== 'null' && html !== null && (<TermsAndConditionsDialog />)}
        <Box sx={styles.mapviewLayoutLeft}>
          <PageToolbar
            showMapview={showMapview}
            showMapviewClick={showMapviewClick}
            showGridviewClick={showGridviewClick}
          />
          <DndContext onDragEnd={handleDragEnd}>
            <Box sx={listWrapperStyle}>
              {visibleFolders.length > 0 && (
                <>
                  <Typography variant="h5" sx={styles.listTitle}>
                    Folders
                  </Typography>
                  <Box sx={styles.gridList}>
                    {visibleFolders.map((folder: IFolder) => (
                      <FolderListItem
                        showMapview={showMapview}
                        folder={folder}
                        key={folder._id}
                      />
                    ))}
                  </Box>
                </>
              )}
              {list.length > 0 && (
                <>
                  {isSearching ? (
                    <Typography variant="body1" sx={styles.resultsCaption}>
                      {resultsCaption}
                    </Typography>
                  ) : (
                    <Typography variant="h5" sx={styles.listTitle}>
                      {PROJECTS_SECTION_TITLE[effectiveOrderSortFilter]}
                    </Typography>
                  )}
                  <Box sx={listStyle}>
                    {list.map((project: IProject) => (
                      <ProjectListItem
                        project={project}
                        key={project._id}
                        handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                      />
                    ))}
                  </Box>
                  <Box sx={styles.loading}>
                    {!projectsLoading && (loadAll ? (
                      <Typography variant="h3">End of List</Typography>
                    ) : (
                      <>
                        <InView onChange={(inView) => {
                          if (inView) {
                            dispatch(SetPageAction(page + 1));
                            dispatch(GetProjectsThunk());
                          }
                        }}
                        />
                        <Typography variant="h3">Loading more...</Typography>
                      </>
                    ))}
                  </Box>
                </>
              )}
              {projectsLoading && (
                <Box sx={styles.loading}>
                  <CircularProgress size={50} />
                </Box>
              )}
            </Box>
          </DndContext>
          {
            emptyState === 'no-projects' && (
              <Box sx={styles.noProjects}>
                <Typography variant="h1">
                  You don't currently have any projects. Create a
                  <br />
                  project to get started!
                </Typography>
              </Box>
            )
          }
          {
            emptyState === 'not-found' && (
              <Box sx={styles.noProjects}>
                <Typography variant="h1">{isSearching ? notFoundMessage : 'Project not found'}</Typography>
              </Box>
            )
          }
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectList;
